// ════════════════════════════════════════════════════════════════════
// src/storeVisits/storeVisitReports.js   (SVR Phase 3, Sprint 3.1)
//
// ADMIN-side Visit Reports: a filterable list of store visits + drill-in
// to the read-only detail. Lives in admin.html (which already loads the
// storeVisits bundle for the targets tab).
//
// Entry point:  window.renderVisitReports()  — called on tab switch.
//   Renders into  #visit-reports-root  (drop that div wherever the tab lives).
//
// Reuse:
//   - Detail view: window.openStoreVisitDetail(id) (from storeVisitView.js)
//   - Endpoint:    GET /api/store-visits  (lightweight list, token-gated for
//                  the unscoped "all" read — admin/area hold a token)
//
// Scoping (admin role model — bare `currentAdmin`, like the staff app's
// bare `currentUser`):
//   admin (HO/GM)  → all stores
//   area_manager   → their area's stores, hand-overs included (coverAwareAreaStores)
//   store_manager  → [currentAdmin.store]
//
// Small dataset (28 stores × a few visits/mo): fetch once, filter client-side.
// v136.9: an area manager's area includes any area handed over to them for today (src/shared/area-cover.js), and
// the visits they WROTE themselves stay listed even when the store is outside it (read only, their own work; the
// Worker's list rule is the same).
// ════════════════════════════════════════════════════════════════════
import { coverAwareAreaStores } from '../shared/area-cover.js';
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

const RAG_PILL = {
  green: { bg: '#10b981', label: 'Green' },
  amber: { bg: '#f59e0b', label: 'Amber' },
  red:   { bg: '#ef4444', label: 'Red' },
};
const STATUS_PILL = {
  draft:     { text: 'Draft',     bg: '#9ca3af' },
  submitted: { text: 'Submitted', bg: '#3b82f6' },
  reviewed:  { text: 'Reviewed',  bg: '#10b981' },
};

function getToken() {
  return adminToken();   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
}
function getWorkerUrl() {
  // Bare DB, not window.DB (db.js uses let/const in shared script scope).
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Asia/Dubai',
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return String(iso); }
}
function ragPill(rag) {
  const r = RAG_PILL[rag];
  if (!r) return '<span style="font-size:12px;color:var(--sub,#6b7280);">No rating</span>';
  return '<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:12px;' +
    'font-weight:700;color:#fff;background:' + r.bg + ';">' + r.label + '</span>';
}
function statusPill(status) {
  const s = STATUS_PILL[status] || { text: status || 'Unknown', bg: '#9ca3af' };
  return '<span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:11px;' +
    'font-weight:700;color:#fff;background:' + s.bg + ';">' + escapeHtml(s.text) + '</span>';
}

// ── Role scoping ────────────────────────────────────────────────────────
// Returns null = no restriction (all stores); array = restrict to these names.
function scopedStores() {
  const u = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
  if (!u) return null;
  if (u.role === 'admin') return null;
  if (u.role === 'area_manager') {
    return (typeof AREA_STORE_MAP !== 'undefined') ? coverAwareAreaStores(u, AREA_STORE_MAP) : [];   // v136.9: + hand-overs
  }
  if (u.role === 'store_manager') return u.store ? [u.store] : [];
  return null;
}

// ── Data ────────────────────────────────────────────────────────────────
let _allVisits = [];   // last fetch (already store-scoped to the user)
const _filters = { store: '', area: '', country: '', month: '', am: '', status: '', rag: '', actioned: '' };

// store -> { area, areaName, country } via the live area→stores map + ALL_AREAS.
// Built lazily (after areas/stores have loaded) and cached. v124.0.
let _storeMeta = null;
function buildStoreMeta() {
  const m = {};
  try {
    const asm = (typeof getAreaStoreMap === 'function') ? getAreaStoreMap() : {};
    const byId = {};
    ((typeof ALL_AREAS !== 'undefined') ? ALL_AREAS : []).forEach(function (a) { byId[String(a.id)] = a; });
    Object.keys(asm).forEach(function (areaId) {
      const a = byId[String(areaId)] || {};
      (asm[areaId] || []).forEach(function (st) {
        m[st] = { area: String(areaId), areaName: a.name || ('Area ' + areaId), country: a.country || '' };
      });
    });
  } catch (e) {}
  return m;
}
function metaFor(store) {
  if (!_storeMeta) _storeMeta = buildStoreMeta();
  return _storeMeta[store] || { area: '', areaName: '', country: '' };
}

async function fetchVisits() {
  const res = await fetch(getWorkerUrl() + '/api/store-visits', { cache: 'no-store',
    headers: { 'Authorization': 'Bearer ' + getToken() },
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error('Load failed: ' + res.status + ' ' + t.slice(0, 120));
  }
  const all = await res.json();
  const scope = scopedStores();
  if (!scope) return all || [];
  const set = new Set(scope);
  const me = (typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.id != null) ? String(currentAdmin.id) : null;
  // v136.9: plus the visits this person wrote, wherever they were (read only)
  return (all || []).filter(function (v) { return set.has(v.store) || (me != null && v.visited_by_id != null && String(v.visited_by_id) === me); });
}

function applyFilters(visits) {
  return visits.filter(function (v) {
    if (_filters.store && v.store !== _filters.store) return false;
    if (_filters.area && metaFor(v.store).area !== _filters.area) return false;
    if (_filters.country && metaFor(v.store).country !== _filters.country) return false;
    if (_filters.status && v.status !== _filters.status) return false;
    if (_filters.rag && v.overall_rag !== _filters.rag) return false;
    if (_filters.am && (v.visited_by_name || '') !== _filters.am) return false;
    if (_filters.actioned === 'yes' && !v.store_actioned_at) return false;
    if (_filters.actioned === 'no' && v.store_actioned_at) return false;
    if (_filters.month) {
      const d = v.entry_time || v.created_at || '';
      // entry_time is ISO; compare the YYYY-MM in Dubai time
      let ym = '';
      try {
        const dt = new Date(d);
        const parts = dt.toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit' });
        ym = parts.slice(0, 7); // en-CA gives YYYY-MM-DD
      } catch (e) { ym = String(d).slice(0, 7); }
      if (ym !== _filters.month) return false;
    }
    return true;
  });
}

// ── Rendering ─────────────────────────────────────────────────────────────
function selectHtml(field, label, options, current, allLabel) {
  const opts = ['<option value="">' + escapeHtml(allLabel || ('All ' + label.toLowerCase())) + '</option>']
    .concat(options.map(function (o) {
      const val = o.value != null ? o.value : o;
      const txt = o.label != null ? o.label : o;
      return '<option value="' + escapeHtml(val) + '"' + (String(val) === String(current) ? ' selected' : '') + '>' +
        escapeHtml(txt) + '</option>';
    }));
  return '<label style="display:flex;flex-direction:column;gap:3px;font-size:11px;font-weight:700;color:var(--sub,#6b7280);text-transform:uppercase;letter-spacing:.4px;">' +
    escapeHtml(label) +
    '<select data-filter="' + field + '" style="padding:7px 8px;border:1px solid var(--border,#d1d5db);border-radius:7px;font-size:13px;background:var(--input-bg);min-width:130px;">' +
    opts.join('') + '</select></label>';
}

function filterBarHtml() {
  const scope = scopedStores();
  const storeOpts = (scope || (typeof ALL_STORES !== 'undefined' ? ALL_STORES : [])).slice().sort();
  const amSet = {};
  _allVisits.forEach(function (v) { if (v.visited_by_name) amSet[v.visited_by_name] = true; });
  const amOpts = Object.keys(amSet).sort();
  // Area + country options derived from the loaded (in-scope) visits.
  const areaMap = {}, countrySet = {};
  _allVisits.forEach(function (v) {
    const mm = metaFor(v.store);
    if (mm.area) areaMap[mm.area] = mm.areaName;
    if (mm.country) countrySet[mm.country] = true;
  });
  const areaOpts = Object.keys(areaMap).sort().map(function (id) { return { value: id, label: areaMap[id] }; });
  const countryOpts = Object.keys(countrySet).sort();
  return '<div style="display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end;margin-bottom:16px;">' +
    selectHtml('store', 'Store', storeOpts, _filters.store, 'All stores') +
    selectHtml('area', 'Area', areaOpts, _filters.area, 'All areas') +
    selectHtml('country', 'Country', countryOpts, _filters.country, 'All countries') +
    '<label style="display:flex;flex-direction:column;gap:3px;font-size:11px;font-weight:700;color:var(--sub,#6b7280);text-transform:uppercase;letter-spacing:.4px;">Month' +
      '<input type="month" data-filter="month" value="' + escapeHtml(_filters.month) + '" ' +
      'style="padding:6px 8px;border:1px solid var(--border,#d1d5db);border-radius:7px;font-size:13px;"></label>' +
    selectHtml('am', 'Visited by', amOpts, _filters.am, 'All') +
    selectHtml('status', 'Status', ['draft', 'submitted', 'reviewed'], _filters.status, 'All statuses') +
    selectHtml('rag', 'RAG', [{ value: 'green', label: 'Green' }, { value: 'amber', label: 'Amber' }, { value: 'red', label: 'Red' }], _filters.rag, 'All') +
    selectHtml('actioned', 'Actioned', [{ value: 'yes', label: 'Actioned' }, { value: 'no', label: 'Not actioned' }], _filters.actioned, 'All') +
    '<button type="button" id="svr-rep-reset" style="padding:8px 12px;border:1px solid var(--border,#d1d5db);background:var(--card);border-radius:7px;font-size:13px;cursor:pointer;">Clear</button>' +
  '</div>';
}

function actionedChip(v) {
  // v129.83 — the store's RESPONSE state, at a glance: declined beats
  // completed beats acknowledged (a decline needs the AM's eyes first).
  const updates = Array.isArray(v.action_updates) ? v.action_updates : [];
  if (updates.some(function (u) { return u && u.kind === 'declined'; })) {
    return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;color:#fff;background:#ef4444;">⛔ Declined</span>';
  }
  if (updates.some(function (u) { return u && (u.all_done || u.kind === 'completed'); })) {
    return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;color:#fff;background:#059669;">✅ Completed</span>';
  }
  if (v.store_actioned_at) {
    return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;color:#fff;background:#10b981;">✓ Acknowledged</span>';
  }
  return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;color:var(--sub);background:var(--surface-3);border:1px solid var(--border);">Not acknowledged</span>';
}

// Area managers + head office may delete (soft) any report. Store managers may not.
function isAreaOrHeadOffice() {
  const u = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
  return !!u && (u.role === 'admin' || u.role === 'area_manager');
}

function cardHtml(v) {
  const when = fmtDate(v.entry_time || v.created_at);
  // v131.79 (Matt: "on the svr, show the area manager and staff members face").
  // visited_by_id is the AM ACCOUNT id (storeVisitReports.js draft-ownership
  // check relies on exactly that), so the face is a chip the sweep hydrates.
  // Older rows recorded only the NAME - those keep initials, which is the
  // honest rendering: we do not know WHICH account it was.
  const whoFace = (v.visited_by_id != null && typeof Avatars !== 'undefined' && Avatars.chip)
    ? Avatars.chip('account', v.visited_by_id, v.visited_by_name, 18) + ' '
    : '';
  const who = whoFace + escapeHtml(v.visited_by_name || 'Unknown');
  const type = v.visit_type === 'short' ? 'Short visit' : 'Detailed visit';
  const store = escapeHtml(v.store || 'Unknown store');
  const plan = (v.action_plan || '').trim();
  const planHtml = plan
    ? '<div style="margin-top:6px;font-size:13px;color:var(--text,#111);line-height:1.4;">' +
        '<span style="font-weight:700;color:var(--sub,#6b7280);">Action plan: </span>' + escapeHtml(plan) + '</div>'
    : '';
  // Delete is offered to area managers / head office on ANY report (soft-delete —
  // recoverable + audited). Store managers never see it. It's a <span> (not a nested
  // <button>) with stopPropagation so it doesn't open the detail. v124.1 / v126.
  const delHtml = isAreaOrHeadOffice()
    ? ' <span class="svr-rep-del" data-del-id="' + escapeHtml(v.id) + '" title="Delete this report (recoverable)" ' +
        'style="color:var(--danger);font-weight:700;cursor:pointer;white-space:nowrap;">🗑 Delete</span>'
    : '';
  // Force-submit a stuck DRAFT (AM/HO only) — pushes an area manager's unfinished
  // draft through to 'submitted' so the store can see it. Same stopPropagation
  // pattern as Delete. Only shows on drafts.
  const submitHtml = (isAreaOrHeadOffice() && v.status === 'draft')
    ? ' <span class="svr-rep-submit" data-submit-id="' + escapeHtml(v.id) + '" title="Force-submit this draft" ' +
        'style="color:var(--ok);font-weight:700;cursor:pointer;white-space:nowrap;">✓ Submit</span>'
    : '';
  return '<button type="button" class="svr-rep-card" data-id="' + escapeHtml(v.id) + '" ' +
    'style="display:block;width:100%;text-align:left;cursor:pointer;background:var(--card);' +
    'border:1px solid var(--border,#e5e7eb);border-radius:10px;padding:13px 14px;margin-bottom:9px;">' +
      '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">' +
        '<div style="font-weight:800;font-size:14px;line-height:1.25;">' + store + '</div>' + ragPill(v.overall_rag) +
      '</div>' +
      '<div style="margin-top:4px;font-size:12px;color:var(--sub,#6b7280);">' +
        escapeHtml(when) + ' · ' + type + ' · ' + who + ' ' + statusPill(v.status) + ' ' + actionedChip(v) + submitHtml + delHtml +
      '</div>' + planHtml +
      '<div style="margin-top:7px;font-size:12px;color:var(--info-deep);font-weight:600;">View full report →</div>' +
    '</button>';
}

// v129.115 (an area manager, 13 Aug: "SVR made at home does not reach the store").
//
// A report only reaches the store at status 'submitted' — the tokenless store
// read is `status=in.(submitted,reviewed)` by design, so an unfinished DRAFT is
// invisible to them and always has been. The force-submit control below has
// existed since v123.x for exactly this, and its own comment says so: "for when
// an area manager left an SVR unfinished so the store never saw it."
//
// So the tool was there and the failure still recurred — because nothing TOLD
// the author. It relied on someone scrolling a filtered list and noticing a
// small green "✓ Submit" on one card. That is the same shape as the field lesson
// from this morning: a control nobody is pointed at is not a control, it is a
// chore. This is the missing half — the draft announces itself.
//
// Scoped to the VIEWER's own drafts when they are an area manager (it is their
// report to finish); Head Office sees any, since chasing them is the HO job.
export function ownUnsubmittedDrafts(visits, viewer) {
  const v = viewer || {};
  const role = String(v.role || '').toLowerCase();
  if (role !== 'admin' && role !== 'area_manager') return [];
  return (Array.isArray(visits) ? visits : []).filter(function (x) {
    if (!x || x.status !== 'draft' || x.deleted_at) return false;
    if (role === 'admin') return true;
    // An AM's own: match on the account id, falling back to the name when the
    // id was not recorded (older rows).
    if (x.visited_by_id != null && v.id != null) return String(x.visited_by_id) === String(v.id);
    return !!(x.visited_by_name && v.name && x.visited_by_name === v.name);
  });
}

function draftBannerHtml() {
  const viewer = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
  const drafts = ownUnsubmittedDrafts(_allVisits, viewer);
  if (!drafts.length) return '';
  const rows = drafts.slice(0, 8).map(function (d) {
    return '<div style="display:flex;align-items:center;gap:8px;margin-top:5px;font-size:12.5px">' +
      '<span style="flex:1">' + escapeHtml(d.store || 'A store') +
      (d.entry_time ? ' <span style="color:var(--warn-deep)">· ' + escapeHtml(String(d.entry_time).slice(0, 10)) + '</span>' : '') + '</span>' +
      '<span class="svr-rep-submit" data-submit-id="' + escapeHtml(d.id) + '" ' +
      'style="color:var(--ok);font-weight:800;cursor:pointer;white-space:nowrap">✓ Submit now</span></div>';
  }).join('');
  const more = drafts.length > 8 ? '<div style="font-size:11.5px;margin-top:5px">+ ' + (drafts.length - 8) + ' more.</div>' : '';
  return '<div style="background:var(--warn-bg);border:1px solid var(--learn-warn-line);border-radius:10px;padding:12px 14px;margin-bottom:14px;color:var(--warn-deep)">' +
    '<div style="font-weight:800;font-size:13.5px">⚠️ ' + drafts.length + ' report' + (drafts.length === 1 ? '' : 's') +
    ' not submitted — the store cannot see ' + (drafts.length === 1 ? 'it' : 'them') + ' yet</div>' +
    '<div style="font-size:11.5px;margin-top:2px">A report only reaches the store once it is submitted.</div>' +
    rows + more + '</div>';
}

function renderList(root) {
  const filtered = applyFilters(_allVisits);
  const listHtml = filtered.length
    ? filtered.map(cardHtml).join('')
    : '<div style="padding:28px 4px;color:var(--sub,#6b7280);font-size:14px;">No reports match these filters.</div>';
  root.innerHTML =
    // v129.115 — ABOVE the filter bar deliberately: a draft hidden behind
    // whatever filters are set is the situation being fixed.
    draftBannerHtml() +
    filterBarHtml() +
    '<div style="font-size:12px;color:var(--sub,#6b7280);margin-bottom:10px;">' +
      filtered.length + ' report' + (filtered.length === 1 ? '' : 's') +
      (filtered.length !== _allVisits.length ? ' (of ' + _allVisits.length + ')' : '') +
    '</div>' +
    '<div id="svr-rep-list">' + listHtml + '</div>';
  wire(root);
}

function wire(root) {
  root.querySelectorAll('[data-filter]').forEach(function (el) {
    const ev = el.tagName === 'SELECT' ? 'change' : 'input';
    el.addEventListener(ev, function () {
      _filters[el.dataset.filter] = el.value;
      renderList(root);
    });
  });
  const reset = root.querySelector('#svr-rep-reset');
  if (reset) reset.addEventListener('click', function () {
    _filters.store = _filters.area = _filters.country = _filters.month = _filters.am = _filters.status = _filters.rag = _filters.actioned = '';
    renderList(root);
  });
  root.querySelectorAll('.svr-rep-card').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (typeof window.openStoreVisitDetail === 'function') window.openStoreVisitDetail(btn.dataset.id);
    });
  });
  // Delete control (AM/HO, any status) — stopPropagation so it doesn't open the detail view.
  root.querySelectorAll('.svr-rep-del').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.stopPropagation();
      e.preventDefault();
      deleteReport(el.dataset.delId, root);
    });
  });
  // Force-submit control (AM/HO, drafts only) — same stopPropagation pattern.
  root.querySelectorAll('.svr-rep-submit').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.stopPropagation();
      e.preventDefault();
      submitReport(el.dataset.submitId, root);
    });
  });
}

// Soft-delete a report (any status — AM/HO only). The worker DELETE /api/store-visits/:id
// sets deleted_at + deleted_by and audits it; the list GET filters deleted_at=is.null, so it
// won't reappear on reload. Reversible in the DB if a deletion was a mistake. v124.1 / v126.
async function deleteReport(id, root) {
  if (!id) return;
  const v = _allVisits.find(function (x) { return x.id === id; });
  const label = v ? (v.store || 'this report') : 'this report';
  const extra = (v && v.status && v.status !== 'draft') ? ' (a ' + v.status + ' report)' : '';
  if (!window.confirm('Delete the report for "' + label + '"' + extra + '? It will be hidden from the list. This is recoverable.')) return;
  try {
    const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(id), { cache: 'no-store',
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer ' + getToken() },
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    _allVisits = _allVisits.filter(function (x) { return x.id !== id; });
    renderList(root);
  } catch (e) {
    alert('Couldn\u2019t delete that report: ' + (e && e.message ? e.message : e));
  }
}

// Force-submit a stuck DRAFT (AM/HO only). PATCH status='submitted' — the worker
// stamps submitted_at and notifies the area manager / Head Office, exactly as a
// normal submit. For when an area manager left an SVR unfinished so the store
// never saw it. v123.x
async function submitReport(id, root) {
  if (!id) return;
  const v = _allVisits.find(function (x) { return x.id === id; });
  const label = v ? (v.store || 'this report') : 'this report';
  if (!window.confirm('Force-submit the draft report for "' + label + '"?\nIt becomes a submitted report (visible to the store) and notifies the area manager / Head Office.')) return;
  try {
    const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(id), { cache: 'no-store',
      method: 'PATCH',
      headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'submitted' }),
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const updated = await res.json().catch(function () { return null; });
    if (v) { v.status = 'submitted'; if (updated && updated.submitted_at) v.submitted_at = updated.submitted_at; }
    renderList(root);
  } catch (e) {
    alert('Could not submit that report: ' + (e && e.message ? e.message : e));
  }
}

async function renderVisitReports() {
  const root = document.getElementById('visit-reports-root');
  if (!root) { console.warn('[storeVisitReports] #visit-reports-root not found'); return; }
  root.innerHTML = '<div style="padding:28px 4px;color:var(--sub,#6b7280);font-size:14px;">Loading reports…</div>';
  try {
    _allVisits = await fetchVisits();
    renderList(root);
  } catch (e) {
    root.innerHTML = '<div style="padding:28px 4px;color:var(--ui-bad);font-size:14px;">Couldn\u2019t load reports. ' +
      escapeHtml(e.message) + '</div>';
  }
}

window.renderVisitReports = renderVisitReports;
window.reloadVisitReports = renderVisitReports;

export { renderVisitReports };
